import React, { useState } from 'react';
import Tooltip from '../components/ui/Tooltip';
import { ROW_BUTTON } from './DocSection';
import type { Adoption, PlanPoint } from './pointsTypes';

// Named for the same reason as PointRow's: the section around this block is a `group` too.
const LINE_REVEAL =
  'opacity-0 group-hover/adopted:opacity-100 group-focus-within/adopted:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-150 ease-emphasized motion-reduce:transition-none';

const authorName = (p: PlanPoint | undefined) =>
  p ? `${p.author.firstname || ''} ${p.author.lastname || ''}`.trim() || 'A former member' : null;

/** A seal with a check through it, filled. The one gold mark in the points panel. */
export const AdoptedIcon: React.FC<{ className?: string }> = ({ className = 'h-3.5 w-3.5' }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className={`flex-shrink-0 ${className}`} fill="none">
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M12 2 14.4 4.1 17.6 3.8 18.5 6.9 21.4 8.4 20.4 11.5 21.4 14.6 18.5 16.1 17.6 19.2 14.4 18.9 12 21 9.6 18.9 6.4 19.2 5.5 16.1 2.6 14.6 3.6 11.5 2.6 8.4 5.5 6.9 6.4 3.8 9.6 4.1ZM10.6 15.6 16.7 9.5 15.3 8.1 10.6 12.8 8.7 10.9 7.3 12.3Z"
    />
  </svg>
);

interface AdoptedBlockProps {
  adoptions: Adoption[];
  pointsById: Map<string, PlanPoint>;
  /** A manager or this topic's authority, on a plan that is not locked or archived. */
  canUnadopt: boolean;
  onUnadopt: (pointId: string) => Promise<void>;
  /** Hands each line's element to the panel, which glides a newly adopted point into it. */
  registerLine: (pointId: string, el: HTMLElement | null) => void;
}

/**
 * What the topic has settled on, above the discussion that produced it. Each line
 * is the wording at the moment it was adopted, so a later edit cannot change a decision.
 */
const AdoptedBlock: React.FC<AdoptedBlockProps> = ({ adoptions, pointsById, canUnadopt, onUnadopt, registerLine }) => {
  const [busy, setBusy] = useState<string | null>(null);
  if (adoptions.length === 0) return null;

  return (
    <section aria-label="Adopted" className="rounded-lg bg-surface-container-high p-2 ring-1 ring-primary/30">
      <h3 className="flex items-center gap-1.5 px-2 pt-0.5 text-xs font-semibold text-primary">
        <AdoptedIcon />
        Adopted
      </h3>
      <ul className="mt-1 space-y-0.5">
        {adoptions.map((a) => {
          const author = authorName(pointsById.get(a.point_id));
          const byline = [author, a.adopted_by_name ? `adopted by ${a.adopted_by_name}` : 'adopted'].filter(Boolean).join(', ');
          return (
            <li
              key={a.id}
              ref={(el) => registerLine(a.point_id, el)}
              className="group/adopted relative flex items-start gap-2 rounded-md px-2 py-1.5"
            >
              {/* The landing glow. Opacity only, animated by the panel when this line arrives. */}
              <span data-land aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-md bg-primary/15 opacity-0" />
              <div className="relative min-w-0 flex-1">
                <p className="max-w-[65ch] whitespace-pre-wrap break-words text-[13px] leading-relaxed text-on-surface">{a.body_snapshot}</p>
                <div className="text-[11px] text-on-surface-variant">{byline.charAt(0).toUpperCase() + byline.slice(1)}</div>
              </div>
              {canUnadopt && (
                <Tooltip label="Take this note back out of the topic. It stays in the discussion.">
                  <button
                    type="button"
                    disabled={busy === a.point_id}
                    onClick={async () => {
                      setBusy(a.point_id);
                      await onUnadopt(a.point_id);
                      setBusy(null);
                    }}
                    aria-label={`Unadopt: ${a.body_snapshot.slice(0, 60)}`}
                    className={`${ROW_BUTTON} relative flex-shrink-0 ${LINE_REVEAL}`}
                  >
                    Unadopt
                  </button>
                </Tooltip>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default AdoptedBlock;
